import { Component, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MockOrder, MOCK_ORDERS } from '../mock-data/mock-orders.data';

/**
 * Expandable Rows Example
 * Demonstrates master-detail expandable rows functionality
 */
@Component({
  selector: 'app-expandable-rows-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Filas Expandibles (Master-Detail)</h2>
        <p class="text-gray-600 mb-2">
          Haz clic en el ícono de expansión para ver el detalle de productos de cada pedido.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            🔽 Row Expansion
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            📦 Master-Detail
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            ⚡ Animated
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            📊 Order Details
          </span>
        </div>

        <!-- Control Buttons -->
        <div class="flex items-center gap-4 mb-4 flex-wrap">
          <button mat-raised-button color="primary" (click)="expandAll()">
            <mat-icon>unfold_more</mat-icon>
            Expandir Todo
          </button>

          <button mat-raised-button color="accent" (click)="collapseAll()">
            <mat-icon>unfold_less</mat-icon>
            Colapsar Todo
          </button>

          <div class="p-3 bg-blue-50 dark:bg-blue-900 rounded">
            <p class="text-sm">
              <strong>Filas expandidas:</strong> {{ getExpandedCount() }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          #grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig"
          [detailTemplate]="detailTemplate">
        </app-smart-grid>
      </div>

      <!-- Detail Template -->
      <ng-template #detailTemplate let-order>
        <div class="p-6 bg-gray-50 dark:bg-gray-800">
          <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
            <mat-icon class="text-blue-600">shopping_cart</mat-icon>
            Detalle del Pedido: {{ order.serial }}
          </h3>

          <!-- Order Summary -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div class="bg-white dark:bg-gray-700 p-4 rounded shadow-sm">
              <div class="text-sm text-gray-500 dark:text-gray-400">Cliente</div>
              <div class="font-semibold">{{ order.soldTo.name }}</div>
              <div class="text-xs text-gray-500">{{ order.soldTo.code }}</div>
            </div>

            <div class="bg-white dark:bg-gray-700 p-4 rounded shadow-sm">
              <div class="text-sm text-gray-500 dark:text-gray-400">Fecha de Entrega</div>
              <div class="font-semibold">
                {{ order.deliveryDateTime | date: 'short' }}
              </div>
            </div>

            <div class="bg-white dark:bg-gray-700 p-4 rounded shadow-sm">
              <div class="text-sm text-gray-500 dark:text-gray-400">Total del Pedido</div>
              <div class="text-xl font-bold text-green-600">
                {{ order.total.toFixed(2) }}
              </div>
            </div>
          </div>

          <!-- Order Items Table -->
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-gray-200 dark:bg-gray-700">
                  <th class="text-left p-3 font-semibold">Pos.</th>
                  <th class="text-left p-3 font-semibold">Código</th>
                  <th class="text-left p-3 font-semibold">Material</th>
                  <th class="text-right p-3 font-semibold">Cantidad</th>
                  <th class="text-right p-3 font-semibold">Precio Unit.</th>
                  <th class="text-right p-3 font-semibold">Subtotal</th>
                  <th class="text-right p-3 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of order.detail"
                    class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <td class="p-3">{{ item.position }}</td>
                  <td class="p-3 font-mono text-xs">{{ item.material.code }}</td>
                  <td class="p-3">{{ item.material.name }}</td>
                  <td class="p-3 text-right">{{ item.soldQuantity }}</td>
                  <td class="p-3 text-right">{{ item.unitPrice.toFixed(2) }}</td>
                  <td class="p-3 text-right">{{ item.subTotal.toFixed(2) }}</td>
                  <td class="p-3 text-right font-semibold">{{ item.total.toFixed(2) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="bg-gray-100 dark:bg-gray-700 font-bold">
                  <td colspan="5" class="p-3 text-right">Subtotal:</td>
                  <td colspan="2" class="p-3 text-right">{{ order.subTotal.toFixed(2) }}</td>
                </tr>
                <tr class="bg-gray-100 dark:bg-gray-700">
                  <td colspan="5" class="p-3 text-right">Descuentos:</td>
                  <td colspan="2" class="p-3 text-right text-red-600">- {{ order.totalDiscounts.toFixed(2) }}</td>
                </tr>
                <tr class="bg-gray-100 dark:bg-gray-700">
                  <td colspan="5" class="p-3 text-right">Impuestos (15%):</td>
                  <td colspan="2" class="p-3 text-right">{{ order.totalTaxes.toFixed(2) }}</td>
                </tr>
                <tr class="bg-green-100 dark:bg-green-900 font-bold text-lg">
                  <td colspan="5" class="p-3 text-right">Total:</td>
                  <td colspan="2" class="p-3 text-right text-green-700 dark:text-green-300">
                    {{ order.total.toFixed(2) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Additional Info -->
          <div *ngIf="order.note || order.purchaseOrder" class="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded">
            <h4 class="font-semibold mb-2">Información Adicional:</h4>
            <p *ngIf="order.note" class="text-sm mb-1"><strong>Nota:</strong> {{ order.note }}</p>
            <p *ngIf="order.purchaseOrder" class="text-sm"><strong>Orden de Compra:</strong> {{ order.purchaseOrder }}</p>
          </div>
        </div>
      </ng-template>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">✨ Características de filas expandibles:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>Master-Detail:</strong> Muestra información adicional sin navegar a otra página</li>
          <li><strong>Animaciones fluidas:</strong> Transiciones suaves al expandir/colapsar</li>
          <li><strong>Templates personalizables:</strong> Define tu propia vista de detalle con ng-template</li>
          <li><strong>Expansión múltiple:</strong> Permite expandir varias filas simultáneamente</li>
          <li><strong>Click en ícono:</strong> Controla la expansión con el botón dedicado</li>
          <li><strong>API programática:</strong> Expande/colapsa filas desde código</li>
          <li><strong>Integración perfecta:</strong> Funciona con filtros, ordenamiento y paginación</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">🎯 Casos de uso:</h3>
        <ul class="text-sm space-y-2 list-disc list-inside">
          <li><strong>Detalle de órdenes:</strong> Muestra items/productos de un pedido</li>
          <li><strong>Información ampliada:</strong> Revela datos adicionales sin cambiar de vista</li>
          <li><strong>Documentos relacionados:</strong> Lista facturas, entregas u otros documentos asociados</li>
          <li><strong>Historial:</strong> Muestra timeline de cambios o eventos de un registro</li>
          <li><strong>Acciones contextuales:</strong> Añade botones y acciones específicas del registro</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class ExpandableRowsExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  orders: MockOrder[] = MOCK_ORDERS.slice(0, 20); // Use first 20 orders for demo

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 300,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 150,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 150,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 10,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
    },
    search: {
      enabled: true,
      showSearchInput: true,
    },
    rowExpansion: {
      enabled: true,
      multipleExpand: true, // Allow multiple rows expanded at once
      expandOnClick: false, // Only expand via icon, not row click
      expandColumn: {
        pinned: 'left',
        width: 50,
      },
    },
    styling: {
      maxHeight: '600px',
    },
  };

  /**
   * Expand all rows
   */
  expandAll(): void {
    this.grid.api.expandAll();
  }

  /**
   * Collapse all rows
   */
  collapseAll(): void {
    this.grid.api.collapseAll();
  }

  /**
   * Get count of expanded rows
   */
  getExpandedCount(): number {
    return this.grid?.api?.getExpandedRows().length ?? 0;
  }

  exampleCode = `// Component Code
import { ViewChild, TemplateRef } from '@angular/core';
import { SmartGridComponent } from 'app/shared/components/smart-grid';

export class ExpandableRowsExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  gridConfig: GridConfig<MockOrder> = {
    rowExpansion: {
      enabled: true,                  // Enable row expansion
      multipleExpand: true,            // Allow multiple rows expanded
      expandOnClick: false,            // Only expand via icon click
      expandColumn: {
        pinned: 'left',                // Pin expand column to left
        width: 50,
      },
    },
  };

  // Programmatic control
  expandAll(): void {
    this.grid.api.expandAll();
  }

  collapseAll(): void {
    this.grid.api.collapseAll();
  }

  getExpandedCount(): number {
    return this.grid.api.getExpandedRows().length;
  }
}

// Template
<app-smart-grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig"
  [detailTemplate]="detailTemplate">
</app-smart-grid>

<!-- Detail Template -->
<ng-template #detailTemplate let-order>
  <div class="p-6">
    <h3>Detalle del Pedido: {{ order.serial }}</h3>

    <!-- Your custom detail view here -->
    <table>
      <tr *ngFor="let item of order.detail">
        <td>{{ item.material.name }}</td>
        <td>{{ item.soldQuantity }}</td>
        <td>{{ item.total }}</td>
      </tr>
    </table>
  </div>
</ng-template>

// Features:
// - Master-detail view without navigation
// - Smooth expand/collapse animations
// - Customizable detail template
// - Multiple rows expanded simultaneously
// - Programmatic API control
// - Works with filters, sorting, and pagination
// - Context-aware detail rendering`;
}
